// Common kitchen measurement conversions
export const KITCHEN_UNITS = {
    TEASPOON: 'tsp',
    TABLESPOON: 'tbsp',
    CUP: 'cup',
    HALF_CUP: '1/2cup',
    QUARTER_CUP: '1/4cup',
    GRAM: 'g',
    KILOGRAM: 'kg',
    MILLILITER: 'ml',
    LITER: 'l',
    UNIT: 'unit',
    WHOLE: 'whole'
};

// Arrays for unit type checking
export const VOLUME_UNITS = ['ml', 'l', 'tsp', 'tbsp', 'cup', 'cups', '1/2cup', '1/4cup', '1/2 cup', '1/4 cup', '½cup', '¼cup', '½ cup', '¼ cup'];
export const MASS_UNITS = ['g', 'kg'];

// Conversion factors to base units (ml or g)
export const UNIT_CONVERSIONS = {
    // Volume conversions (to ml)
    'tsp': 5,
    'tbsp': 15,
    'cup': 250,
    '1/2cup': 125,
    '1/4cup': 62.5,
    '1/2 cup': 125,  // Support with space
    '1/4 cup': 62.5,  // Support with space
    '½cup': 125,      // Support Unicode
    '¼cup': 62.5,     // Support Unicode
    '½ cup': 125,     // Support Unicode with space
    '¼ cup': 62.5,    // Support Unicode with space
    'l': 1000,
    'ml': 1,
    // Mass conversions (to g)
    'kg': 1000,
    'g': 1,
    // Additional unit conversions
    'teaspoon': 5,
    'teaspoons': 5,
    'tablespoon': 15,
    'tablespoons': 15,
    'cups': 250
};

/**
 * Converts a measurement to its base unit (ml or g)
 * @param {number} amount - The amount to convert
 * @param {string} fromUnit - The unit to convert from
 * @returns {number} - The converted amount in base units
 */
export function convertToBaseUnit(amount, fromUnit) {
    // Handle null/undefined cases
    if (!amount || !fromUnit) return amount;

    const unitLower = fromUnit.toLowerCase().trim();
    
    // Handle fractional cups and decimal amounts
    if (unitLower === 'cup' || unitLower === 'cups') {
        // If amount is 0.5, treat as 1/2 cup
        if (amount === 0.5) {
            return 125; // 0.5 cup = 125ml/g
        }
        // If amount is 0.25, treat as 1/4 cup
        if (amount === 0.25) {
            return 62.5; // 0.25 cup = 62.5ml/g
        }
        // Standard cup conversion
        return amount * 250;
    }
    
    // Check for conversion factor in UNIT_CONVERSIONS
    const conversionFactor = UNIT_CONVERSIONS[unitLower];
    if (conversionFactor) {
        return amount * conversionFactor;
    }
    
    // If no conversion needed, return original amount
    return amount;
}

/**
 * Checks if a unit is a volume measurement
 * @param {string} unit - The unit to check
 * @returns {boolean}
 */
export function isVolumeUnit(unit) {
    return VOLUME_UNITS.includes(unit?.toLowerCase());
}

/**
 * Checks if a unit is a mass measurement
 * @param {string} unit - The unit to check
 * @returns {boolean}
 */
export function isMassUnit(unit) {
    return MASS_UNITS.includes(unit?.toLowerCase());
}

/**
 * Gets the base unit type (g or ml) for a given unit
 * @param {string} unit - The unit to check
 * @returns {string} - Returns 'g' for mass units, 'ml' for volume units, or the original unit
 */
export function getBaseUnitType(unit) {
    if (isVolumeUnit(unit)) return 'ml';
    if (isMassUnit(unit)) return 'g';
    return unit;
}
